import { CSSProperties } from "react";
import { Ambiance } from "../domain/types";

/**
 * The panoramas that are a flat picture behind the board rather than geometry
 * inside it.
 *
 * A grove modelled in 3D is the wrong answer twice over: culms near the key
 * light throw bars across the table, and anything with real depth competes
 * with the figures for attention when the whole point is a calm backdrop. So
 * the illustration sits in the DOM, under the transparent canvas, exactly as
 * the meditation timer does it — the same drawing, the same white veil that
 * keeps whatever is in front of it readable.
 *
 * Lives apart from ConstellationCanvas on purpose: the workspace needs this
 * while the canvas is still lazy-loading, and importing it from there would
 * drag three.js into the main bundle.
 */
const BACKDROPS: Partial<Record<Ambiance, string>> = {
    bamboo: "/images/board/scene-bamboo.svg",
};

/** The white veil the illustration sits under, so figures stay the thing you
 *  read. Same value the marketing site uses for its scenes. */
const VEIL = "linear-gradient(rgba(255, 255, 255, 0.82), rgba(255, 255, 255, 0.82))";

/** True when this panorama is painted in the DOM, so the canvas must clear
 *  transparent instead of filling itself with a sky colour. */
export function hasBackdrop(ambiance: Ambiance | undefined): boolean {
    return !!ambiance && ambiance in BACKDROPS;
}

/** Background style for the board container, or nothing for a panorama that
 *  paints itself inside the scene. */
export function boardBackdropStyle(ambiance: Ambiance | undefined): CSSProperties {
    const image = ambiance ? BACKDROPS[ambiance] : undefined;
    if (!image) return {};
    return {
        backgroundImage: `${VEIL}, url("${image}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
    };
}
